import { useLazyQuery, useQuery } from '@apollo/client';
import {
  adminWaitlistColumns,
  WaitlistAlgoliaHit,
} from '@website/components/admin/AdminWaitlist/adminWaitlist';
import DataTable from '@website/components/admin/DataTable';
import FilterByTrips from '@website/components/admin/TripsDropdownFilter';
import AlgoliaPagination from '@website/components/search/algolia/AlgoliaPagination';
import TextSearch from '@website/components/trips/search/filters/TextSearch';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  AdminSearchKeyDocument,
  DownloadWaitlistCsvDocument,
} from '@website/types/graphqlOperations';
import algoliasearch from 'algoliasearch';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Configure, connectHits, InstantSearch } from 'react-instantsearch-dom';
import AdminLayout from '../../components/layouts/adminLayout';

function WaitlistItemsResults({ hits }: { hits: WaitlistAlgoliaHit[] }) {
  const [download, { loading }] = useLazyQuery(DownloadWaitlistCsvDocument, {
    fetchPolicy: 'network-only',
  });

  const downloadCsv = useCallback(async () => {
    const { data } = await download();
    const { downloadUrl } = data.exportWaitListToCSV;
    // Create a link and click it to download the file
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'waitlist.csv';
    document.body.appendChild(link);
    link.click();
  }, [download]);

  return (
    <Row>
      <Col xs={12} className="py-2 align-items-end">
        <Button
          disabled={loading}
          className="align-self-end"
          onClick={downloadCsv}
        >
          {loading ? <>Downloading...</> : <> Download CSV</>}
        </Button>
      </Col>
      <Col xs={12}>
        <DataTable data={hits} columns={adminWaitlistColumns} />
      </Col>
    </Row>
  );
}

export const SearchItemResultsConnected = connectHits(
  WaitlistItemsResults as any,
);

export function AdminReviews() {
  const { data, loading, error } = useQuery(AdminSearchKeyDocument, {
    // Don't use the cache as the key is sensitive
    fetchPolicy: 'network-only',
    // Reload every 1 hour (technically 59 minutes as the key validity is 60 minutes)
    pollInterval: 1000 * 59 * 60,
  });

  if (error) return <ErrorPage error={error} />;

  if (loading || !data) return <LoadingPage />;

  const searchClient = algoliasearch(
    data.search.algoliaAdminID,
    data.search.algoliaAdminKey,
  );

  const startOfCurrentHour = DateTime.utc().startOf('hour').toMillis() / 1000;

  return (
    <Container fluid className="pt-3">
      <InstantSearch searchClient={searchClient} indexName="waitlist">
        <Row>
          <Col xs={12} md={6} lg={4} xxl={3} className="mb-3">
            <TextSearch />
          </Col>
          <Col xs={12} md={6} lg={4} xxl={3} className="mb-3">
            <FilterByTrips
              operator="or"
              attribute="group.trip.id"
              limit={100}
            />
          </Col>
        </Row>
        <Configure
          hitsPerPage={100}
          filters={`groupStartTimestamp>${startOfCurrentHour}`}
        />
        <SearchItemResultsConnected />
        <AlgoliaPagination />
      </InstantSearch>
    </Container>
  );
}

export default function AdminDashboardPage() {
  return (
    <AdminLayout>
      <AdminReviews />
    </AdminLayout>
  );
}
